"use client";

import { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { MarkdownRenderer } from "@/components/markdown-renderer";
import { useTranslations } from "next-intl";

interface CollapsibleDescriptionProps {
  description: string;
  title: string;
}

export function CollapsibleDescription({ description, title }: CollapsibleDescriptionProps) {
  const t = useTranslations("common");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el || isExpanded) return;

    const check = () => setIsClamped(el.scrollHeight > el.clientHeight);
    check();

    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [description, isExpanded]);

  return (
    <div>
      <h2 className="text-xl font-semibold mb-3">{title}</h2>
      <div className="prose prose-sm max-w-none">
        <div
          ref={contentRef}
          className={!isExpanded ? "line-clamp-3" : undefined}
        >
          <MarkdownRenderer
            content={description}
            className="text-muted-foreground [--tw-prose-bullets:var(--muted-foreground)] [--tw-prose-counters:var(--muted-foreground)]"
          />
        </div>
        {isClamped && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-2 text-sm text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1"
          >
            {isExpanded ? (
              <>
                {t("actions.showLess")} <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                {t("actions.showMore")} <ChevronDown className="h-4 w-4" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
